/**
 * Execution-Source scope selector.
 *
 * The single source of truth for which slice of trading data the UI is
 * looking at. Persists via URL param (?execution_source=…) first, then
 * localStorage, then defaults. Every analytics GET request auto-scopes
 * via the api.js interceptor.
 *
 * Values:
 *   - ALPACA_PAPER        broker-simulated (paper) trades only
 *   - INTERNAL_PAPER_MOCK internal mock trades only
 *   - ALL                 combined view (shows a warning)
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "tm_execution_source";
const EXPLICIT_KEY = "tm_execution_source_explicit";
const URL_PARAM = "execution_source";
const EVENT = "tm:execution-source-changed";
const BROKER_EVENT = "tm:broker-changed";
const VALID = new Set(["ALPACA_PAPER", "INTERNAL_PAPER_MOCK", "ALL"]);

export const BROKER_CHANGED_EVENT = BROKER_EVENT;

export const EXECUTION_SOURCES = [
  { id: "ALPACA_PAPER",         label: "Alpaca Paper",     dot: "🟢" },
  { id: "INTERNAL_PAPER_MOCK",  label: "Internal Paper",   dot: "🟡" },
  { id: "ALL",                  label: "All",              dot: "⚪" },
];

/** Map broker `mode` → matching execution source scope. */
export function brokerModeToSource(mode) {
  if (mode === "alpaca_paper") return "ALPACA_PAPER";
  if (mode === "mock") return "INTERNAL_PAPER_MOCK";
  return "ALL";
}

function _readUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const v = p.get(URL_PARAM);
    return v && VALID.has(v) ? v : null;
  } catch { return null; }
}

function _writeUrl(next) {
  try {
    const url = new URL(window.location.href);
    if (next && next !== "ALL") url.searchParams.set(URL_PARAM, next);
    else url.searchParams.delete(URL_PARAM);
    window.history.replaceState({}, "", url.toString());
  } catch { /* ignore */ }
}

function _isExplicit() {
  try { return localStorage.getItem(EXPLICIT_KEY) === "1"; }
  catch { return false; }
}
function _setExplicit(flag) {
  try {
    if (flag) localStorage.setItem(EXPLICIT_KEY, "1");
    else localStorage.removeItem(EXPLICIT_KEY);
  } catch { /* ignore */ }
}

export function getExecutionSource() {
  // URL first (shareable, reproducible). If found, sync to localStorage so
  // subsequent page navigations preserve the choice without needing the
  // URL param on every route.
  const fromUrl = _readUrl();
  if (fromUrl) {
    try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch { /* ignore */ }
    _setExplicit(true);       // URL param counts as explicit
    return fromUrl;
  }
  return localStorage.getItem(STORAGE_KEY) || "ALL";
}

export function setExecutionSource(next, { explicit = true } = {}) {
  if (!next) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, next);
  _setExplicit(!!explicit);
  // Only mirror to the URL for explicit picks — auto-follow silently
  // updates localStorage so the URL stays clean and a fresh visit
  // still gets the broker-mapped default.
  if (explicit) _writeUrl(next);
  else _writeUrl(null);
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { value: next, explicit } }));
}

/** Auto-follow: only overrides when the user hasn't explicitly picked. */
export function syncExecutionSourceToBroker(mode) {
  if (_isExplicit()) return null;
  const target = brokerModeToSource(mode);
  if (getExecutionSource() === target) return null;
  setExecutionSource(target, { explicit: false });
  return target;
}

/** Reset explicit-choice flag so the source starts following the broker again. */
export function resetExecutionSourceOverride() { _setExplicit(false); }

/** Broadcast that the broker mode changed — listeners auto-sync the source. */
export function notifyBrokerChanged(mode) {
  window.dispatchEvent(new CustomEvent(BROKER_EVENT, { detail: { mode } }));
}

export function useExecutionSource() {
  const [source, setSource] = useState(getExecutionSource);
  useEffect(() => {
    const handler = (e) => setSource(e.detail?.value || "ALL");
    window.addEventListener(EVENT, handler);
    // Sync when browser Back/Forward changes URL params.
    const popHandler = () => setSource(getExecutionSource());
    window.addEventListener("popstate", popHandler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("popstate", popHandler);
    };
  }, []);
  const setter = useCallback((next) => {
    setExecutionSource(next, { explicit: true });
    setSource(next);
    if (next === "ALL") {
      toast.warning("Combined view — mixes Alpaca broker-simulated and Internal Paper mock results");
    }
  }, []);
  return [source, setter];
}

/** Exported so consumers can read the explicit-choice flag if they need it. */
export function isExecutionSourceExplicit() { return _isExplicit(); }
